import React, { useEffect } from 'react';
import { Menu, Bell } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as PigeonsSymbole } from '../../assets/images/pigeons_symbole.svg';
import { AccountCard } from './AccountCard';
import { DashboardCard } from './DashboardCard';
import LanguageSwitcher from '../LanguageSwitcher';
import { useProfileStore } from '../../stores/profileStore';
import { useTranslation } from 'react-i18next';
import { ROLES } from '../../types/auth';
import { useUIStore } from '../../stores/uiStore';
import { Button } from '../common/Button';

interface MobileNavigationProps {
  isOpen: boolean;
  onToggle: (forceClose?: boolean) => void;
  onSignOut: () => void;
  notifications: number;
  renderNavLinks: (variant: 'alternate' | 'primary') => React.ReactNode;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isOpen,
  onToggle,
  onSignOut,
  notifications,
  renderNavLinks,
}) => {
  const { profile } = useProfileStore();
  const { t } = useTranslation('common');
  const { hideNavigationElements } = useUIStore();
  const location = useLocation();
  const isOnSearchPage =
    location.pathname === '/freelancers' || location.pathname === '/job-search';
  // Handle body overflow when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const renderTopBar = () => (
    <>
      <Link to="/" className="text-2xl font-bold" onClick={() => onToggle(true)}>
        <PigeonsSymbole
          className={`h-12 ${isOpen ? 'text-white' : 'text-[#052A37]'}`}
          aria-label="Pigeons Logo"
        />
      </Link>

      <div data-testid="mobile-top-bar-actions" className="flex items-center gap-4">
        {!isOpen && <LanguageSwitcher />}

        {profile && (
          <div className="flex items-center gap-8">
            {!isOpen && !hideNavigationElements && (
              <>
                <Link
                  to="/notifications"
                  className="relative flex h-10 w-10 items-center justify-center rounded-full"
                >
                  <Bell className="h-5 w-5 text-secondary" />
                  {notifications > 0 && (
                    <span className="absolute -top-1 right-0 rounded-full bg-primary px-1.5 py-0.5 text-xs text-white">
                      {notifications}
                    </span>
                  )}
                </Link>
                <div className="flex items-center gap-1 text-secondary">
                  <span className="text-sm">{t('mobileNavigation.myCredits')}</span>
                  <span className="text-sm font-bold">0</span>
                </div>
              </>
            )}
            <button onClick={() => onToggle()} className="z-40 -m-4 min-h-0 min-w-0 p-4">
              <div className="flex h-5 w-5 items-center justify-center">
                {isOpen ? (
                  <div className="h-0.5 w-5 bg-primary-inverse" />
                ) : (
                  <Menu className="h-6 w-6 text-gray-600" />
                )}
              </div>
            </button>
          </div>
        )}
      </div>
    </>
  );

  const renderLoggedInMainMenu = () => (
    <section className="w-full">
      {!!profile && (
        <div className="mb-2 text-sm font-bold text-white">{t('mobileNavigation.mainMenu')}</div>
      )}
      <div
        className={`bg-transparent p-6 flex flex-col gap-2 ${profile ? 'border border-white rounded-3xl' : ''}`}
      >
        {/* Navigation Links */}
        <div className="flex flex-col items-center gap-8">{renderNavLinks('alternate')}</div>

        <div className="mt-6 flex w-full justify-center">
          <LanguageSwitcher className="!text-white" />
        </div>
      </div>
    </section>
  );

  const renderLoggedOutMainMenu = () => (
    <>
      <div className="flex flex-col items-center gap-8">{renderNavLinks('alternate')}</div>
      <div className="flex w-full justify-center pb-8">
        <LanguageSwitcher className="!text-white" />
      </div>
    </>
  );

  return (
    <div className="flex w-full items-center justify-between md:hidden">
      {!isOpen && renderTopBar()}
      {/* Mobile Menu Overlay */}
      <div
        className={`
          fixed inset-0 bg-[#2D3648] z-30 
          flex flex-col items-center justify-between pt-6 pb-14 px-4
          transition-transform duration-300 ease-in-out
          overflow-y-auto max-h-screen
          ${isOpen ? 'transform translate-x-0' : 'transform translate-x-full'}
        `}
      >
        <div className="flex w-full items-center justify-between">{isOpen && renderTopBar()}</div>

        {/* Notification bell and search bar */}
        {profile && !hideNavigationElements && (
          <div className="mb-4 mt-6 flex w-full flex-col gap-6">
            <Link
              to="/notifications"
              className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#E1E9F0]"
              onClick={() => onToggle(true)}
            >
              <Bell className="h-5 w-5 text-secondary" />
              {notifications > 0 && (
                <span className="absolute -right-2 -top-2 rounded-full bg-primary px-1.5 py-0.5 text-xs text-white">
                  {notifications}
                </span>
              )}
            </Link>
            {profile && profile.role !== 'administrator' && !isOnSearchPage && (
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="primary"
                  className="whitespace-nowrap"
                  href={profile.role === ROLES.RECRUITER ? '/freelancers' : '/job-search'}
                  onClick={() => onToggle(true)}
                >
                  {profile.role === ROLES.RECRUITER
                    ? t('navigation.findFreelancer')
                    : t('navigation.findProject')}
                </Button>
                {profile.role === ROLES.FREELANCER && (
                  <Button
                    variant="alternate"
                    className="whitespace-nowrap"
                    href="/freelancers"
                    onClick={() => onToggle(true)}
                  >
                    {t('navigation.findFreelancer')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Account, dashboard, and main menu cards */}
        {profile && (
          <div className="mt-8 flex w-full flex-col gap-4">
            <AccountCard
              firstName={profile?.first_name}
              lastName={profile?.last_name}
              avatarUrl={profile?.avatar_url}
              onNavigate={onToggle}
              onLogout={onSignOut}
            />
            <DashboardCard prospectsCount={2} onNavigate={onToggle} />
          </div>
        )}

        {profile ? renderLoggedInMainMenu() : renderLoggedOutMainMenu()}
      </div>
    </div>
  );
};
